import { type Instant, parseInstant } from '@taler/domain'
import type { OperationQuery, Schemas } from '../contract/types.ts'
import type { TalerClient } from '../http/client.ts'
import { type CacheKey, type Invalidations, ledgerKey } from '../http/keys.ts'
import { projectQuery, type QueryProjection, queryFingerprint } from '../http/query.ts'
import type { Page } from './page.ts'

export interface Merchant {
  readonly id: string
  readonly name: string
  readonly aliases: readonly string[]
  readonly createdAt: Instant
  readonly updatedAt: Instant
}

export interface MerchantMergeResult {
  readonly destinationId: string
  readonly movedEntries: number
}

export type MerchantListQuery = OperationQuery<'merchants_list'>

const MERCHANT_LIST_PROJECTION: QueryProjection<MerchantListQuery> = {
  limit: (value) => String(value),
  cursor: (value) => value,
  search: (value) => value,
}

export const merchantKeys = {
  list: (ledgerId: string, query: MerchantListQuery): CacheKey =>
    ledgerKey(ledgerId, 'merchants', 'list', queryFingerprint(projectQuery(query, MERCHANT_LIST_PROJECTION))),
  detail: (ledgerId: string, merchantId: string): CacheKey =>
    ledgerKey(ledgerId, 'merchants', 'detail', merchantId),
  /** Everything an alias or merge write makes stale. */
  invalidatedByWrite: (ledgerId: string): Invalidations => [ledgerKey(ledgerId, 'merchants')],
} as const

export interface MerchantsRepository {
  list(ledgerId: string, query?: MerchantListQuery, signal?: AbortSignal): Promise<Page<Merchant>>
  get(ledgerId: string, merchantId: string, signal?: AbortSignal): Promise<Merchant>
  /** Refused when `alias` already names this merchant or collides with a different one. */
  addAlias(ledgerId: string, merchantId: string, alias: string): Promise<Merchant>
  /** Idempotent - removing an alias that isn't there is a no-op, mirroring account hide/unhide. */
  removeAlias(ledgerId: string, merchantId: string, alias: string): Promise<Merchant>
  /**
   * Moves every entry from `sourceMerchantId` to `destinationMerchantId` and deletes the
   * source, in one transaction - irreversible.
   */
  merge(
    ledgerId: string,
    sourceMerchantId: string,
    destinationMerchantId: string,
  ): Promise<MerchantMergeResult>
}

/**
 * List-only for creation: there is no `merchants_get`/`create`/`update` operation in the
 * contract - a merchant is a side effect of a journal write's own `payee` (myWealth spec
 * §1.1), never created here directly. Its alias list and its merge into another merchant
 * are curated once it exists.
 */
export function createMerchantsRepository(client: TalerClient): MerchantsRepository {
  const base = (ledgerId: string) => `/api/v1/ledgers/${encodeURIComponent(ledgerId)}/merchants`
  const merchant = (ledgerId: string, merchantId: string) =>
    `${base(ledgerId)}/${encodeURIComponent(merchantId)}`

  return {
    async list(ledgerId, query = {}, signal) {
      const response = await client.call<Schemas['PageResponseMerchantResponse']>({
        method: 'GET',
        path: base(ledgerId),
        query: projectQuery(query, MERCHANT_LIST_PROJECTION),
        ...(signal === undefined ? {} : { signal }),
      })
      return { items: response.items.map(toMerchant), nextCursor: response.nextCursor }
    },

    async get(ledgerId, merchantId, signal) {
      return toMerchant(
        await client.call<Schemas['MerchantResponse']>({
          method: 'GET',
          path: merchant(ledgerId, merchantId),
          ...(signal === undefined ? {} : { signal }),
        }),
      )
    },

    async addAlias(ledgerId, merchantId, alias) {
      return toMerchant(
        await client.call<Schemas['MerchantResponse'], Schemas['MerchantAliasRequest']>({
          method: 'POST',
          path: `${merchant(ledgerId, merchantId)}/aliases`,
          body: { alias },
        }),
      )
    },

    async removeAlias(ledgerId, merchantId, alias) {
      return toMerchant(
        await client.call<Schemas['MerchantResponse'], Schemas['MerchantAliasRequest']>({
          method: 'DELETE',
          path: `${merchant(ledgerId, merchantId)}/aliases`,
          body: { alias },
        }),
      )
    },

    async merge(ledgerId, sourceMerchantId, destinationMerchantId) {
      const response = await client.call<Schemas['MerchantMergeResponse'], Schemas['MergeMerchantRequest']>({
        method: 'POST',
        path: `${merchant(ledgerId, sourceMerchantId)}/merge`,
        body: { destinationId: destinationMerchantId },
      })
      return { destinationId: response.destinationId, movedEntries: response.movedEntries }
    },
  }
}

function toMerchant(response: Schemas['MerchantResponse']): Merchant {
  return {
    id: response.id,
    name: response.name,
    aliases: response.aliases,
    createdAt: parseInstant(response.createdAt),
    updatedAt: parseInstant(response.updatedAt),
  }
}
